import { CeruleanButton } from 'components/Button'
import CurrencyLogo from 'components/CurrencyLogo'
import I18n from 'components/I18n'
import CloseCircle from 'components/Icons/CloseCircle'
import { actions, selectors } from 'data'
import messages from 'lang'
import React from 'react'
import { connect, ConnectedProps, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { color } from 'utils'
import { getBaseCurrency, getCounterCurrency } from 'utils/pairs'

const Wrapper = styled.div`
  padding: 2rem 2.5rem;
  width: 30rem;
  background: ${color('dark800')};
  color: ${color('white')};
`

const Close = styled(CloseCircle)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
`

const Header = styled.header`
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-gap: 0.5rem 1rem;

  > :first-child {
    grid-row: 1 / 3;
  }
`

const SymbolWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  > :first-child {
    z-index: 1;
    margin-right: 3rem;
    border: 4px solid ${color('dark800')};
    background: ${color('dark800')};
    border-radius: 50%;
    box-sizing: content-box;
  }

  > :nth-child(2) {
    position: absolute;
    left: 3rem;
  }
`

const Pair = styled.span`
  font-weight: 600;
  font-size: 1rem;
  line-height: 150%;
`

const Tag = styled.span`
  border: 1px solid ${color('tierGold')};
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  color: ${color('warningMuted')};
  width: fit-content;
`

const Title = styled.h1`
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 150%;
`

const Description = styled.p`
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.25rem;
`

const Button = styled(CeruleanButton)`
  width: 100%;
`

type OwnProps = {
  onClose?: () => void
  pair?: string
}
type Props = ConnectedProps<typeof connector> & OwnProps
const PairAnnouncement = ({
  currencies,
  instruments,
  onClose: propsOnClose,
  options,
  pair: propsPair
}: Props) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const onClose = propsOnClose ?? (() => dispatch(actions.app.hideModal('NEW_MARGIN_PAIR')))
  const pair = options?.pair ?? propsPair
  if (!pair) return null
  const instrument = instruments.find((i) => i.symbol === pair)
  if (!instrument) return null

  const maxLeverage = (instrument.leverage_ratio ?? [5]).reduce((a, b) => Math.max(a, b), 0)
  const base = currencies.find((c) => c.symbol === getBaseCurrency(pair))
  const counter = currencies.find((c) => c.symbol === getCounterCurrency(pair))
  if (!base || !counter) return null

  return (
    <Wrapper>
      <Close onClick={onClose} size='1.5rem' />
      <Header>
        <SymbolWrapper>
          <CurrencyLogo currency={base.displaySymbol} size='3.5rem' />
          <CurrencyLogo currency={counter.displaySymbol} size='3.5rem' />
        </SymbolWrapper>
        <Pair>
          {base.displaySymbol}-{counter.displaySymbol}
        </Pair>
        <Tag>
          <I18n data={messages.margin_new_currency_upto} leverage={maxLeverage} />
        </Tag>
      </Header>
      <Title>
        <I18n
          data={messages.margin_new_currency_title}
          base={base.displaySymbol}
          counter={counter.displaySymbol}
        />
      </Title>
      <Description>
        <I18n
          data={messages.margin_new_currency_description}
          baseCode={base.displaySymbol}
          baseName={base.name}
          counterCode={counter.displaySymbol}
          counterName={counter.name}
          leverage={maxLeverage}
        />
      </Description>
      <Button
        onClick={() => {
          navigate(`/trade/${pair}`)
          onClose()
        }}
      >
        <I18n data={messages.margin_new_currency_button} />
      </Button>
    </Wrapper>
  )
}

const mapStateToProps = (state) => ({
  currencies: selectors.currencies.getCurrencies(state),
  instruments: selectors.instrument.getInstruments(state),
  options: selectors.app.getModalOptions(state)
})

const connector = connect(mapStateToProps)

export default connector(PairAnnouncement)
